import React, { useState, useRef } from "react";
import styled from "styled-components";
import { Upload, X, User, Loader2 } from "lucide-react";
import { Button as BaseButton } from "../common/Button";
import { updateUserAvatar } from "../../services/auth";
import { auth } from "../../services/firebase"; // Import auth from firebase

const StyledButton = styled(BaseButton)`
  margin-top: 10px;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    .overlay {
      opacity: 1;
    }
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DefaultAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);

  svg {
    width: 60px;
    height: 60px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: var(--color-text-primary);
`;

const FileInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 0.9rem;
  margin-top: var(--spacing-2);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface AvatarUploadProps {
  currentPhotoURL?: string;
  onAvatarUpdate: (url: string) => void;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentPhotoURL,
  onAvatarUpdate,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      setError("Aucun fichier sélectionné");
      return;
    }

    // Debugging: Log file information
    console.log("File:", {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    // Validate file type with better error handling
    if (!file.type) {
      setError("Type de fichier non reconnu");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError(`Type de fichier non supporté: ${file.type}. Formats acceptés: JPG, PNG, GIF, WEBP`);
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setError(`Taille du fichier (${(file.size / 1024 / 1024).toFixed(2)}MB) dépasse la limite de 2MB`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Vérifier que le fichier est bien une image
      const img = new Image();
      const imgPromise = new Promise((resolve, reject) => {
        img.onload = () => resolve(true);
        img.onerror = () => reject(new Error("Le fichier n'est pas une image valide"));
      });

      const reader = new FileReader();
      reader.readAsDataURL(file);
      await new Promise((resolve, reject) => {
        reader.onload = () => {
          img.src = reader.result as string;
          resolve(true);
        };
        reader.onerror = () => reject(new Error("Erreur lors de la lecture du fichier"));
      });

      await imgPromise;

      const userId = auth.currentUser?.uid;
      if (!userId) throw new Error('User not authenticated');
      
      await updateUserAvatar(userId, currentPhotoURL || '');
      onAvatarUpdate(currentPhotoURL || '');
    } catch (err: any) {
      console.error("Upload error:", err);
      if (err?.code === "storage/unauthorized") {
        setError("Vous n'êtes pas autorisé à télécharger des images");
      } else if (err?.code === "storage/canceled") {
        setError("Téléchargement annulé");
      } else if (err?.code === "storage/unknown") {
        setError("Une erreur inconnue s'est produite. Veuillez réessayer");
      } else {
        setError(err?.message || "Échec du téléchargement de l'image. Veuillez réessayer");
      }
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Reset input
      }
    }
  };

  return (
    <div>
      <AvatarContainer onClick={handleClick}>
        {currentPhotoURL ? (
          <AvatarImage src={currentPhotoURL} alt="User avatar" />
        ) : (
          <DefaultAvatar>
            <User />
          </DefaultAvatar>
        )}

        {!loading && (
          <Overlay className="overlay">
            <Upload size={24} />
          </Overlay>
        )}

        {loading && (
          <LoadingOverlay>
            <Loader2 size={24} />
          </LoadingOverlay>
        )}

        <FileInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
      </AvatarContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {currentPhotoURL && (
        <StyledButton
          variant="secondary"
          icon={<X size={20} />}
          onClick={() => onAvatarUpdate("")}
        >
          Supprimer l'avatar
        </StyledButton>
      )}
    </div>
  );
};
